import "../style.scss";

import classNames from "classnames";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import InfoTooltip from "components/shared/InfoTooltip";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { TinyBody } from "helpers/TypographyComponents";
import type { FocusEventHandler } from "react";
import { forwardRef } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface TextFormComponentProps {
    label?: string;
    placeholder?: string | null;
    control?: any;
    controlName?: string;
    error?: any;
    required?: boolean;
    disabled?: boolean;
    customRequiredMessage?: string;
    customValidate?: (value: string) => boolean;
    sx?: any;
    className?: string;
    size?: "small" | "medium";
    onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    id?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    /**
     * Show (optional) after label
     */
    showOptionalMark?: boolean;
    /**
     * Container style { width: 100% }
     */
    fullWidth?: boolean;
    customValidateMessage?: string;
    /**
     * Tooltip text
     */
    infoTooltip?: { text: string; link?: string; type: "info" | "question" };
    /**
     * Used to show expanded helpdesk button
     */
    helpdeskInfo?: { text: string; link: string };
}

const TextFormComponent = forwardRef<any, TextFormComponentProps>(function TextFormComponent(
    props: TextFormComponentProps,
    ref,
) {
    const {
        label,
        placeholder,
        control,
        controlName,
        error,
        required,
        disabled = false,
        customRequiredMessage,
        customValidate,
        size,
        sx,
        onFocus,
        id,
        showOptionalMark = false,
        fullWidth = false,
        infoTooltip,
        customValidateMessage,
        onChange,
        helpdeskInfo,
        className,
    } = props;
    const [t] = useTranslation();

    const validate = customValidate ? { validate: customValidate } : {};

    const displayErrorMessage = () => {
        if (error) {
            // If the type comes from "validate" and a custom message was defined, display that.
            if (error.type === "validate" && customValidateMessage) {
                return customValidateMessage;
            } else {
                // or display custom message
                return error?.message ?? "";
            }
        } else {
            return "";
        }
    };

    const isControlled = control && controlName;

    // Render label with icons
    const renderLabel = () => {
        if (!label) return null;

        return (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <label className="form-component-label" htmlFor={controlName} ref={ref}>
                    {label}
                    {required && label && "*"}
                    {showOptionalMark && <TinyBody color="grey">({t("NewElement.Optional")})</TinyBody>}
                </label>
                {helpdeskInfo && (
                    <ExpandedHelpdeskIcon text={helpdeskInfo?.text} link={helpdeskInfo?.link} transparent />
                )}
                {infoTooltip && (
                    <InfoTooltip text={infoTooltip.text} iconType={infoTooltip.type} link={infoTooltip.link} />
                )}
            </div>
        );
    };

    // Render StyledTextField with conditional props
    const renderTextField = (fieldProps?: any) => (
        <StyledTextField
            {...fieldProps}
            error={!!error}
            name={controlName}
            fullWidth
            placeholder={placeholder ?? ""}
            helperText={displayErrorMessage()}
            size={size}
            sx={sx}
            onFocus={onFocus}
            disabled={disabled}
            id={id}
            ref={ref}
            onChange={onChange ?? fieldProps?.onChange}
        />
    );

    return (
        <div className={classNames("form-component", className)} style={fullWidth ? { width: "100%" } : {}}>
            {isControlled && (
                <Controller
                    name={controlName}
                    control={control}
                    rules={{
                        required: required
                            ? customRequiredMessage
                                ? customRequiredMessage
                                : t("NewElement.Required")
                            : undefined,
                        ...validate,
                    }}
                    render={({ field }) => (
                        <>
                            {renderLabel()}
                            {renderTextField(field)}
                        </>
                    )}
                />
            )}
            {!isControlled && (
                <>
                    {renderLabel()}
                    {renderTextField()}
                </>
            )}
        </div>
    );
});

export default TextFormComponent;
